<template>
  <div v-if="canShow" ref="menuRef" class="quick-menu">
    <button class="fab" @click="toggleOpen">{{ isOpen ? '×' : '≡' }}</button>

    <div v-if="isOpen" class="panel">
      <div class="panel-title">快速菜单</div>
      <label v-if="showShopSelector" class="shop-field">
        <span>当前餐厅</span>
        <select
          v-model.number="selectedShopId"
          class="shop-select"
          :disabled="shopLoading || visibleShops.length === 0"
          @change="changeShop"
        >
          <option v-if="shopLoading" :value="0" disabled>餐厅加载中...</option>
          <option v-else-if="visibleShops.length === 0" :value="0" disabled>暂无可用餐厅</option>
          <option v-for="shop in visibleShops" :key="shop.shopId" :value="shop.shopId">
            {{ shop.shopName }}
          </option>
        </select>
      </label>
      <button
        v-for="entry in entries"
        :key="entry.name"
        class="menu-item"
        :class="{ active: route.path === entry.path }"
        @click="go(entry.path)"
      >
        <span class="icon">{{ entry.icon }}</span>
        <span>{{ entry.title }}</span>
      </button>

      <button class="menu-item logout" @click="logout">退出登录</button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useAuthStore } from '@/store/auth';
  import { useGlobalMenu } from '@/composables/useGlobalMenu';
  import { fetchShopDetail, fetchShopList } from '@/service/base';
  import type { ShopDetailModel } from '@/model/shop';
  import { resolveAccessibleShopIds } from '@/constants/auth';

  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const isOpen = ref(false);
  const menuRef = ref<HTMLElement | null>(null);
  const visibleShops = ref<ShopDetailModel[]>([]);
  const selectedShopId = ref(authStore.selectedShopId || 0);
  const shopLoading = ref(false);
  let shopLoadRequest = 0;
  const showShopSelector = computed(() => {
    const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
    return accessibleShopIds === null || accessibleShopIds.length > 0;
  });

  const { entries } = useGlobalMenu();

  const canShow = computed(
    () =>
      authStore.isLoggedIn &&
      authStore.hasSystemAccess &&
      route.path !== '/login' &&
      route.path !== '/forbidden',
  );

  watch(
    () => route.fullPath,
    () => {
      isOpen.value = false;
    },
  );

  const toggleOpen = (): void => {
    isOpen.value = !isOpen.value;
  };

  const handleDocumentClick = (event: MouseEvent): void => {
    if (!isOpen.value) return;
    const target = event.target as Node | null;
    if (menuRef.value && target && !menuRef.value.contains(target)) {
      isOpen.value = false;
    }
  };

  const loadScopedShopDetails = async (shopIds: number[]): Promise<ShopDetailModel[]> => {
    const responses = await Promise.all(
      shopIds.map((shopId) => fetchShopDetail({ shopId }).catch(() => undefined)),
    );
    return responses.flatMap((response) =>
      response?.code === 200 && response.result && Number(response.result.shopStatus) === 0
        ? [response.result]
        : [],
    );
  };

  const loadVisibleShops = async (): Promise<void> => {
    const request = ++shopLoadRequest;
    if (!showShopSelector.value) {
      visibleShops.value = [];
      selectedShopId.value = 0;
      return;
    }
    shopLoading.value = true;
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (request !== shopLoadRequest) return;
      if (response.code !== 200) throw new Error(response.msg || '加载餐厅失败');
      const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
      let shops = (response.result?.list || []) as ShopDetailModel[];
      if (accessibleShopIds?.length && shops.length === 0) {
        shops = await loadScopedShopDetails(accessibleShopIds);
      }
      if (request !== shopLoadRequest) return;
      visibleShops.value = accessibleShopIds
        ? shops.filter((shop) => accessibleShopIds.includes(Number(shop.shopId)))
        : shops;
      const selected =
        visibleShops.value.find((shop) => Number(shop.shopId) === authStore.selectedShopId) ||
        visibleShops.value[0];
      selectedShopId.value = selected ? Number(selected.shopId) : 0;
      if (selected && selectedShopId.value !== authStore.selectedShopId) {
        authStore.selectShop(selectedShopId.value);
        window.location.reload();
      }
    } catch (error) {
      const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
      const fallbackShops = accessibleShopIds?.length
        ? await loadScopedShopDetails(accessibleShopIds)
        : [];
      if (request !== shopLoadRequest) return;
      visibleShops.value = fallbackShops;
      if (!visibleShops.value.length) console.error('加载授权餐厅失败', error);
    } finally {
      if (request === shopLoadRequest) shopLoading.value = false;
    }
  };

  watch(
    [() => authStore.isLoggedIn, () => (authStore.user?.roles || []).slice().sort().join('|')],
    ([isLoggedIn]) => {
      if (!isLoggedIn) {
        shopLoadRequest++;
        visibleShops.value = [];
        selectedShopId.value = 0;
        shopLoading.value = false;
        return;
      }
      void loadVisibleShops();
    },
    { immediate: true },
  );

  onMounted(() => {
    document.addEventListener('click', handleDocumentClick);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleDocumentClick);
  });

  const go = async (path: string): Promise<void> => {
    await router.push(path);
  };

  const changeShop = (): void => {
    if (!visibleShops.value.some((shop) => Number(shop.shopId) === selectedShopId.value)) return;
    authStore.selectShop(selectedShopId.value);
    isOpen.value = false;
    window.location.reload();
  };

  const logout = async (): Promise<void> => {
    await authStore.logout();
    await router.replace('/login');
  };
</script>

<style scoped>
  .quick-menu {
    position: fixed;
    right: 16px;
    bottom: 18px;
    z-index: 3000;
  }

  .fab {
    width: 52px;
    height: 52px;
    border: none;
    border-radius: 50%;
    background: #0f172a;
    color: #fff;
    font-size: 26px;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.32);
  }

  .panel {
    position: absolute;
    right: 0;
    bottom: 64px;
    width: 220px;
    max-height: 62vh;
    overflow-y: auto;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 14px 40px rgba(2, 6, 23, 0.22);
    padding: 10px;
  }

  .panel-title {
    font-size: 12px;
    color: #64748b;
    margin: 2px 6px 8px;
    text-align: left;
  }

  .shop-field {
    display: block;
    margin: 0 2px 10px;
    color: #475569;
    font-size: 12px;
    text-align: left;
  }

  .shop-field span {
    display: block;
    margin: 0 4px 5px;
  }

  .shop-select {
    width: 100%;
    min-height: 38px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    color: #1e293b;
    padding: 6px 8px;
    font-size: 14px;
  }

  .menu-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    border-radius: 8px;
    background: #f8fafc;
    color: #1e293b;
    padding: 9px 10px;
    margin-bottom: 6px;
    cursor: pointer;
    text-align: left;
  }

  .menu-item.active {
    background: #e2e8f0;
    font-weight: 600;
  }

  .menu-item.logout {
    margin-top: 4px;
    background: #fee2e2;
    color: #b91c1c;
    justify-content: center;
  }

  .icon {
    width: 20px;
    text-align: center;
  }
</style>
